/**
 * IT Admin → IT training (22 Oct, Rusty): the incident SLA reference at the
 * top of the page — his slide "Priority, SLA and escalation — Incidents" as
 * real HTML: assess Impact, assess Urgency, apply the commitment (the
 * coloured matrix and the response / resolution targets), then who is
 * notified as the SLA time runs. IT staff only (`<ItAdminLayout>`), so SLA
 * times are allowed here. Every number comes from `lib/sla-reference.ts`;
 * the words are UI labels in the reader's language.
 */

import { Alert, Card, Typography } from "@norican/react";
import type { ReactNode } from "react";

import { SectionCard } from "@/components/common";
import { useT } from "@/i18n";

import {
  SLA_ESCALATION,
  SLA_LEVELS,
  SLA_MATRIX,
  SLA_PRIORITIES,
  SLA_TARGETS,
  type SlaEscalationStep,
  type SlaLevel,
  type SlaRecipient,
} from "./lib/sla-reference";
import { PriorityChip, useDuration } from "./sla-parts";

/** The slide's level tags: High navy, Medium blue, Low grey. */
const LEVEL_LOOK: Readonly<Record<SlaLevel, string>> = {
  high: "bg-bg-inverse text-fg-on-inverse",
  medium: "bg-accent text-fg-on-accent",
  low: "bg-brand-grey-600 text-white",
};

/** The slide's escalation chips: blue, blue, amber, navy. */
function stepLook(at: SlaEscalationStep["at"]): string {
  if (at >= 100) {
    return "bg-bg-inverse text-fg-on-inverse";
  }
  if (at >= 75) {
    return "bg-brand-yellow-400 text-brand-blue-700";
  }
  return "bg-accent text-fg-on-accent";
}

const KEY = "itAdmin.training.sla";

function LevelTag({ level }: { level: SlaLevel }) {
  const { t } = useT("tickets");
  return (
    <span
      className={`${LEVEL_LOOK[level]} inline-flex min-w-24 items-center justify-center rounded-md px-3 py-1 text-xs font-bold tracking-wide uppercase`}
    >
      {t(`${KEY}.level.${level}`)}
    </span>
  );
}

function StepHeading({ n, id, children }: { n: number; id: string; children: ReactNode }) {
  return (
    <Typography as="h3" variant="heading-6" id={id} className="flex items-center gap-3">
      <span
        aria-hidden="true"
        className="bg-accent text-fg-on-accent inline-flex size-9 shrink-0 items-center justify-center rounded-full text-base font-bold"
      >
        {n}
      </span>
      {children}
    </Typography>
  );
}

function LabelHeading({
  as = "h4",
  id,
  children,
}: {
  as?: "h3" | "h4";
  id: string;
  children: ReactNode;
}) {
  return (
    <Typography
      as={as}
      variant="caption"
      tone="muted"
      id={id}
      className="font-semibold tracking-wide uppercase"
    >
      {children}
    </Typography>
  );
}

/** Step 1 or 2: the three levels with what each means, and the rule of thumb. */
function AssessCard({ n, kind }: { n: number; kind: "impact" | "urgency" }) {
  const { t } = useT("tickets");
  const id = `sla-ref-${kind}`;
  return (
    <Card
      variant="subtle"
      padding="md"
      className="flex h-full flex-col gap-3"
      role="group"
      aria-labelledby={id}
    >
      <StepHeading n={n} id={id}>
        {t(`${KEY}.reference.${kind}.title`)}
      </StepHeading>
      <dl className="flex flex-col gap-3">
        {SLA_LEVELS.map((level) => (
          <div key={level} className="flex flex-col items-start gap-1">
            <dt>
              <LevelTag level={level} />
            </dt>
            <dd>
              <Typography as="span" variant="body-sm" className="text-pretty">
                {t(`${KEY}.reference.${kind}.${level}`)}
              </Typography>
            </dd>
          </div>
        ))}
      </dl>
      <Typography
        as="p"
        variant="caption"
        className="border-border bg-surface mt-auto rounded-md border px-3 py-1.5 text-center font-semibold"
      >
        {t(`${KEY}.reference.${kind}.note`)}
      </Typography>
    </Card>
  );
}

function Matrix() {
  const { t } = useT("tickets");
  const level = (name: SlaLevel) => t(`${KEY}.level.${name}`);
  return (
    <div className="flex min-w-0 flex-col gap-2">
      <LabelHeading id="sla-ref-matrix">{t(`${KEY}.reference.matrixTitle`)}</LabelHeading>
      <table
        aria-labelledby="sla-ref-matrix"
        className="w-full max-w-md table-fixed border-separate border-spacing-1"
      >
        <thead>
          <tr>
            <td className="text-fg-muted text-center text-xs font-semibold">
              <abbr
                title={`${t(`${KEY}.impact`)} / ${t(`${KEY}.urgency`)}`}
                className="no-underline"
              >
                {t(`${KEY}.reference.matrixCorner`)}
              </abbr>
            </td>
            {SLA_LEVELS.map((urgency) => (
              <th
                key={urgency}
                scope="col"
                className="bg-bg-inverse text-fg-on-inverse rounded-md px-1 py-2 text-sm"
              >
                <span className="sr-only">{t(`${KEY}.urgency`)}: </span>
                {level(urgency)}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {SLA_LEVELS.map((impact) => (
            <tr key={impact}>
              <th scope="row" className="bg-bg-inverse text-fg-on-inverse rounded-md px-1 text-sm">
                <span className="sr-only">{t(`${KEY}.impact`)}: </span>
                {level(impact)}
              </th>
              {SLA_LEVELS.map((urgency) => (
                <td key={urgency} className="p-0">
                  <PriorityChip priority={SLA_MATRIX[impact][urgency]} large />
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Commitments() {
  const { t } = useT("tickets");
  const duration = useDuration();
  return (
    <div className="flex min-w-0 flex-col gap-2">
      <LabelHeading id="sla-ref-commitments">{t(`${KEY}.reference.commitmentsTitle`)}</LabelHeading>
      <table aria-labelledby="sla-ref-commitments" className="w-full border-collapse text-sm">
        <thead>
          <tr className="bg-bg-inverse text-fg-on-inverse">
            <th scope="col" className="rounded-l-md px-2 py-2 text-left">
              {t(`${KEY}.priority`)}
            </th>
            <th scope="col" className="px-2 py-2 text-left">
              {t(`${KEY}.response`)}
            </th>
            <th scope="col" className="rounded-r-md px-2 py-2 text-left">
              {t(`${KEY}.resolution`)}
            </th>
          </tr>
        </thead>
        <tbody>
          {SLA_PRIORITIES.map((priority) => (
            <tr key={priority} className="border-border-subtle border-b last:border-b-0">
              <th scope="row" className="px-2 py-1.5 text-left">
                <PriorityChip priority={priority} />
              </th>
              <td className="px-2 py-1.5 whitespace-nowrap tabular-nums">
                {duration(SLA_TARGETS[priority].response)}
              </td>
              <td className="px-2 py-1.5 whitespace-nowrap tabular-nums">
                {duration(SLA_TARGETS[priority].resolution)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function EscalationStep({ step }: { step: SlaEscalationStep }) {
  const { t } = useT("tickets");
  const who = (names: readonly SlaRecipient[]) =>
    names.map((name) => t(`${KEY}.recipient.${name}`)).join(", ");
  const told = (names: readonly SlaRecipient[], reminder: boolean) =>
    names.length === 0
      ? t(`${KEY}.noNotification`)
      : t(reminder ? `${KEY}.reference.reminded` : `${KEY}.reference.notified`, {
          who: who(names),
        });
  const same =
    step.response.length > 0 &&
    step.response.length === step.resolution.length &&
    step.response.every((name, i) => step.resolution[i] === name);
  const lines: { label: string; text: string }[] = same
    ? [{ label: t(`${KEY}.reference.responseAndResolution`), text: told(step.response, false) }]
    : [
        { label: t(`${KEY}.response`), text: told(step.response, false) },
        { label: t(`${KEY}.resolution`), text: told(step.resolution, step.reminder === true) },
      ];
  return (
    <Card variant="outline" padding="sm" className="flex h-full items-start gap-3">
      <span
        className={`${stepLook(step.at)} inline-flex min-w-14 shrink-0 items-center justify-center rounded-md px-2 py-1 text-sm font-bold tabular-nums`}
      >
        {step.at}%
      </span>
      <span className="flex min-w-0 flex-col gap-0.5">
        {lines.map(({ label, text }) => (
          <Typography key={label} as="span" variant="body-sm" className="text-pretty">
            <span className="text-fg-muted">{label}: </span>
            <span className="font-semibold">{text}</span>
          </Typography>
        ))}
      </span>
    </Card>
  );
}

/** The incident SLA reference, as the first thing on the IT training page. */
export function SlaReferenceCard() {
  const { t } = useT("tickets");
  return (
    <SectionCard
      aria-labelledby="sla-ref-title"
      title={<span id="sla-ref-title">{t(`${KEY}.reference.title`)}</span>}
      description={
        <span className="text-accent-fg-subtle font-semibold">{t(`${KEY}.reference.lead`)}</span>
      }
      accentTone="accent"
      role="region"
    >
      <div className="flex flex-col gap-4">
        <Alert tone="warning" role="note" aria-live="off">
          {t(`${KEY}.reference.alignmentNote`)}
        </Alert>

        <div className="grid gap-4 md:grid-cols-2">
          <AssessCard n={1} kind="impact" />
          <AssessCard n={2} kind="urgency" />
          <Card
            variant="subtle"
            padding="md"
            className="flex flex-col gap-3 md:col-span-2"
            role="group"
            aria-labelledby="sla-ref-apply"
          >
            <StepHeading n={3} id="sla-ref-apply">
              {t(`${KEY}.reference.applyTitle`)}
            </StepHeading>
            <div className="grid gap-6 lg:grid-cols-2">
              <Matrix />
              <Commitments />
            </div>
          </Card>
        </div>

        <section aria-labelledby="sla-ref-escalation" className="flex flex-col gap-2">
          <LabelHeading as="h3" id="sla-ref-escalation">
            {t(`${KEY}.reference.escalationTitle`)}
          </LabelHeading>
          <ol className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
            {SLA_ESCALATION.map((step) => (
              <li key={step.at}>
                <EscalationStep step={step} />
              </li>
            ))}
          </ol>
        </section>

        <Typography
          as="p"
          variant="body-sm"
          className="bg-bg-inverse text-fg-on-inverse rounded-lg px-4 py-3 text-center font-semibold text-pretty"
        >
          {t(`${KEY}.reference.footer`)}
        </Typography>
      </div>
    </SectionCard>
  );
}
